import { html } from '../../mixins/katapult-element.js';
import { KatapultEditorTemplate } from './katapult-editor-template.js';
import { PortalStyle } from './katapult-model-editor-style.js';
import '../katapult-elements/katapult-button.js';
import '../katapult-elements/katapult-icon.js';
import '../katapult-drop-down/katapult-drop-down.js';
import '../paper-header/paper-header.js';
import { Path } from '../../modules/Path.js';

customElements.define(
  'katapult-mobile-options-editor',
  class extends KatapultEditorTemplate {
    static get template() {
      return html`
        ${super.style} ${PortalStyle}

        <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes" data="{{attributeModels}}"></katapult-firebase-worker>

        <paper-table class="settings-table">
          <!-- Hover Attributes -->
          <paper-row expand>
            <div flex grow style="padding: 12px; gap: 24px;">
              <material-icon icon="label"></material-icon>
              <div flex column grow style="gap: 12px;">
                <div flex align-center style="height: 24px;">Hover Attributes</div>
                <div style="font-size: 10pt" italic>
                  These attributes will be displayed in a popup when the user hovers over an item on the map.
                </div>
                <paper-table grow overflow-hidden style="width: 600px;">
                  <paper-row>
                    <paper-input
                      label="Separator"
                      value="[[_itemData.toast_attributes.separator]]"
                      data-path="_itemData.toast_attributes.separator"
                      on-value-changed="updateProperty"
                    ></paper-input>
                  </paper-row>
                </paper-table>
                <paper-table rounded white outline grow overflow-hidden style="width: 600px;">
                  <paper-row short grey>
                    <paper-cell icon></paper-cell>
                    <paper-cell>Attribute</paper-cell>
                    <paper-cell icon>Label</paper-cell>
                    <paper-cell icon></paper-cell>
                  </paper-row>
                  <!-- Body -->
                  <paper-table-scroll on-sort-changed="toastAttributesSortChanged">
                    <template is="dom-repeat" items="[[_itemData.toast_attributes.list]]" mutable-data>
                      <paper-row white divider slot="sortable">
                        <paper-cell drag-handle icon>
                          <material-icon icon="drag_indicator"></material-icon>
                        </paper-cell>
                        <paper-cell>[[camelCase(item)]]</paper-cell>
                        <paper-cell icon-button>
                          <paper-checkbox
                            checked="[[getToastAttributeLabelEnabled(item, _itemData.toast_attributes.labels.*)]]"
                            on-change="toastAttributeLabelCheckboxChange"
                          ></paper-checkbox>
                        </paper-cell>
                        <paper-cell icon-button>
                          <katapult-button iconOnly noBorder icon="delete" on-click="removeToastAttributeButtonClicked"></katapult-button>
                        </paper-cell>
                      </paper-row>
                    </template>
                  </paper-table-scroll>
                  <!-- Add Drop Down -->
                  <paper-row>
                    <paper-cell>
                      <katapult-drop-down
                        label="Add an Attribute"
                        value="[[item.attribute]]"
                        value-path="$key"
                        label-path="$key"
                        label-function="[[camelCase]]"
                        items="[[attributeModels]]"
                        no-label-float
                        on-selected-changed="toastAttributeSelected"
                      ></katapult-drop-down>
                    </paper-cell>
                  </paper-row>
                </paper-table>
              </div>
            </div>
          </paper-row>
          <!-- Mobile Assessments Workflow Options -->
          <paper-row expand>
            <div flex grow style="padding: 12px; gap: 24px;">
              <material-icon icon="smartphone"></material-icon>
              <div flex column grow style="gap: 12px;">
                <div flex align-center style="height: 24px;">Mobile Assessments Workflow</div>
                <paper-table grow overflow-hidden style="width: 600px;">
                  <paper-row expand checkbox pointer>
                    <paper-cell shrink column align-start style="max-width: 400px;">
                      <span>Edit Via Assessment Menu</span>
                      <span style="font-size: 10pt;" italic
                        >Users using the Mobile Assessments Workflow can delete or move nodes via the Assessment Menu</span
                      >
                    </paper-cell>
                    <paper-cell>
                      <paper-checkbox
                        checked="[[_itemData.mobile_assessments.delete_and_move_menu_enabled]]"
                        data-path="_itemData.mobile_assessments.delete_and_move_menu_enabled"
                        on-change="toggleProperty"
                      ></paper-checkbox>
                    </paper-cell>
                  </paper-row>
                </paper-table>
              </div>
            </div>
          </paper-row>
        </paper-table>
      `;
    }

    constructor() {
      super();
      this.defaultInternalItemData = {
        toast_attributes: {
          separator: ';',
          list: ['note', 'description']
        }
      };
    }

    ready() {
      super.ready();
      this.appConfiguration = new AppConfiguration(this);
    }

    setToastAttributeLabel(attribute, value = true) {
      // Permit value to be null, otherwise, convert to boolean.
      value = value == null ? value : Boolean(value);
      const toastAttributesLabelsPath = '_itemData.toast_attributes.labels';
      this.deepSet(Path.join([toastAttributesLabelsPath, attribute]), value);
    }

    getToastAttributeLabelEnabled(attribute) {
      const toastAttributesLabelsPath = '_itemData.toast_attributes.labels';
      return Boolean(this.get(Path.join([toastAttributesLabelsPath, attribute])));
    }

    addToastAttribute(attribute) {
      const toastAttributesListPath = '_itemData.toast_attributes.list';

      // Ensure toastAttributesList exists and is an array.
      if (!Array.isArray(this.get(toastAttributesListPath))) this.deepSet(toastAttributesListPath, []);

      const toastAttributesList = this.get(toastAttributesListPath);

      if (attribute && !toastAttributesList.includes(attribute)) {
        this.push(toastAttributesListPath, attribute);
        this.setToastAttributeLabel(attribute, false);
      }
    }

    toastAttributeLabelCheckboxChange(e) {
      const checkbox = e.currentTarget;
      const attribute = e.model.item;
      this.setToastAttributeLabel(attribute, checkbox.checked);
    }

    toastAttributeSelected(e) {
      const dropdown = e.currentTarget;
      const attribute = e.detail.value;
      this.addToastAttribute(attribute);
      dropdown.value = null;
    }

    removeToastAttributeButtonClicked(e) {
      const index = e.model.index;
      const toastAttributesListPath = '_itemData.toast_attributes.list';
      const removed = this.splice(toastAttributesListPath, index, 1);
      this.setToastAttributeLabel(removed[0], null);
    }

    toastAttributesSortChanged(e) {
      const toastAttributesListPath = '_itemData.toast_attributes.list';
      const removed = this.splice(toastAttributesListPath, e.detail.prevIndex, 1);
      this.splice(toastAttributesListPath, e.detail.index, 0, removed[0]);
    }
  }
);
